import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, InputNumber, Radio, Button, Space, Typography, message, Spin, Divider, Alert } from 'antd';
import { WechatOutlined, AlipayOutlined, CreditCardOutlined, WalletOutlined, IdcardOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { authAPI, rechargeAPI, cardBindAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const POINTS_RATIO = 100;

export type RenewalProductType = 'membership' | 'card' | 'order_assistant';

const PRODUCT_TYPE_TITLES: Record<RenewalProductType, string> = {
  membership: '账户续费',
  card: '卡密续费',
  order_assistant: '接单助手续费',
};

const PRODUCT_TYPE_EXPIRY_LABELS: Record<RenewalProductType, string> = {
  membership: '续费后到期时间',
  card: '续费后卡密到期时间',
  order_assistant: '续费后接单助手到期时间',
};

interface Product {
  id: number;
  productName: string;
  price: number;
  discountRate: number;
  rebateAmount: number;
  renewalUnit: 'day' | 'month' | 'year' | 'permanent';
  renewalQuantity: number;
  isDefault: boolean;
  rebatePoints?: number;
}

interface RenewalConfig {
  renewalUnit: 'day' | 'month' | 'year';
  minRenewalQuantity: number;
  maxRenewalQuantity: number;
  pricePerUnit: number;
  discountRate?: number;
  qrCodeTimeout: number;
  productName: string;
  autoCloseDelay: number;
  wechatEnabled: boolean;
  alipayEnabled: boolean;
  balancePaymentEnabled: boolean;
  wechatAppId?: string;
  alipayAppId?: string;
  products?: Product[];
}

interface RenewalModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  isExpired?: boolean;
  productType?: RenewalProductType;
}

const RenewalModal: React.FC<RenewalModalProps> = ({ visible, onCancel, onSuccess, isExpired = false, productType = 'membership' }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [config, setConfig] = useState<RenewalConfig | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'wechat' | 'alipay' | 'balance'>('wechat');
  const [balance, setBalance] = useState<number>(0);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<{ qrCode: string; orderId: string; paymentType: 'wechat' | 'alipay'; amount?: string } | null>(null);
  const [countdown, setCountdown] = useState(300);
  const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
  const [cardExpireTime, setCardExpireTime] = useState<string | null>(null);
  const [orderAssistantExpiresAt, setOrderAssistantExpiresAt] = useState<string | null>(null);
  const [boundCardKey, setBoundCardKey] = useState<string | null>(null);
  const { updateUser, user } = useAuth();

  useEffect(() => {
    if (visible) {
      fetchRenewalConfig();
      fetchBalance();
      if (productType !== 'membership') {
        fetchBindInfo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, productType]);

  const fetchBindInfo = async () => {
    try {
      const res = await cardBindAPI.getBindInfo();
      const data = res.data?.data;
      if (data) {
        setCardExpireTime(data.cardExpireTime || null);
        setOrderAssistantExpiresAt(data.orderAssistantExpiresAt || null);
        setBoundCardKey(data.boundCardKey || null);
      }
    } catch {
      // 忽略，使用空基准时间
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchBalance = async () => {
    try {
      const response = await rechargeAPI.getBalance();
      if (response.data.status === 200) {
        setBalance(response.data.data.balance);
      }
    } catch (error: any) {
    }
  };

  const fetchRenewalConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await authAPI.getRenewalConfig(productType);
      if (response.data.status === 200 && response.data.data) {
        const cfg = response.data.data;
        setConfig(cfg);
        const defaultPaymentMethod = cfg.balancePaymentEnabled ? 'balance' : 
                                    cfg.wechatEnabled ? 'wechat' : 
                                    cfg.alipayEnabled ? 'alipay' : 'wechat';
        
        let defaultProduct: Product | null = null;
        if (cfg.products && cfg.products.length > 0) {
          defaultProduct = cfg.products.find((p: Product) => p.isDefault) || cfg.products[0];
          if (defaultProduct) {
            setSelectedProductId(defaultProduct.id);
            setSelectedProduct(defaultProduct);
          }
        }
        
        form.setFieldsValue({
          paymentMethod: defaultPaymentMethod,
          productId: defaultProduct?.id,
        });
        setPaymentMethod(defaultPaymentMethod);
      } else {
        message.error(response.data.error || '获取续费配置失败');
      }
    } catch (error: any) {
      console.error('获取续费配置错误:', error);
      message.error(error.response?.data?.error || '获取续费配置失败');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSubmit = async (values: { paymentMethod: 'wechat' | 'alipay' | 'balance'; productId?: number; quantity?: number }) => {
    if (!config) {
      message.error('续费配置未加载，请稍后重试');
      return;
    }

    if (!values.productId) {
      message.error('请选择商品');
      return;
    }

    if (!values.quantity || values.quantity < 1) {
      message.error('请选择数量');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.createRenewalOrder({
        paymentMethod: values.paymentMethod,
        productId: values.productId,
        quantity: values.quantity || 1,
      });

      if (response.data.status === 200) {
        const { orderId, paymentUrl, qrCode, amount, paymentMethod: responsePaymentMethod } = response.data.data;
        
        
        if (responsePaymentMethod === 'balance' || values.paymentMethod === 'balance') {
          message.success('续费成功！');
          setQrCodeModalVisible(false);
          if (updateUser) {
            await updateUser();
          }
          fetchBalance();
          onSuccess();
          return;
        }
        
        if (values.paymentMethod === 'wechat') {
          if (qrCode) {
            setQrCodeData({ qrCode, orderId, paymentType: 'wechat', amount: amount });
            setCountdown(config.qrCodeTimeout || 300);
            setIsQrCodeExpired(false);
            setQrCodeModalVisible(true);
            onCancel();
            pollOrderStatus(orderId);
          } else if (paymentUrl) {
            window.open(paymentUrl, '_blank');
            message.info('正在跳转到支付页面...');
            pollOrderStatus(orderId);
          } else {
            Modal.info({
              title: '订单创建成功',
              width: 500,
              content: (
                <div style={{ padding: '20px 0' }}>
                  <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                    <Text strong style={{ fontSize: '16px' }}>订单已创建，但支付链接生成失败</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>订单号：</Text>
                    <Text copyable>{orderId}</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>续费时长：{selectedProduct ? (selectedProduct.renewalUnit === 'permanent' ? '永久' : `${selectedProduct.renewalQuantity}${selectedProduct.renewalUnit === 'day' ? '天' : selectedProduct.renewalUnit === 'month' ? '个月' : '年'}`) : '未知'}</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>订单金额：</Text>
                    <Text strong style={{ color: '#ff4d4f', fontSize: '18px' }}>¥{amount}</Text>
                  </div>
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff7e6', borderRadius: '4px' }}>
                    <Text type="warning">
                      支付链接生成失败，可能是支付配置不正确。请联系管理员检查支付配置，或稍后重试。
                    </Text>
                  </div>
                </div>
              ),
              onOk: () => {
                pollOrderStatus(orderId);
              },
            });
            pollOrderStatus(orderId);
          }
        } else {
          if (qrCode) {
            setQrCodeData({ qrCode, orderId, paymentType: 'alipay', amount: amount });
            setCountdown(config.qrCodeTimeout || 300);
            setIsQrCodeExpired(false);
            setQrCodeModalVisible(true);
            onCancel();
            pollOrderStatus(orderId);
          } else if (paymentUrl) {
            if (response.data.data.error) {
              Modal.warning({
                title: '支付配置提示',
                width: 500,
                content: (
                  <div style={{ padding: '20px 0' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text>{response.data.data.error}</Text>
                    </div>
                    <div style={{ padding: '12px', background: '#fff7e6', borderRadius: '4px', marginTop: '16px' }}>
                      <Text type="warning" strong>重要提示：</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Text type="warning">
                          如果遇到"ISV权限不足"错误，请检查：<br/>
                          1. 支付宝应用是否已签约"当面付"或"电脑网站支付"产品<br/>
                          2. 签约是否已审核通过并生效<br/>
                          3. 应用状态是否为"上线"状态
                        </Text>
                      </div>
                    </div>
                  </div>
                ),
              });
            }
            window.open(paymentUrl, '_blank');
            message.info('正在跳转到支付页面...');
            pollOrderStatus(orderId);
          } else {
            Modal.info({
              title: '订单创建成功',
              width: 500,
              content: (
                <div style={{ padding: '20px 0' }}>
                  <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                    <Text strong style={{ fontSize: '16px' }}>订单已创建，但支付链接生成失败</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>订单号：</Text>
                    <Text copyable>{orderId}</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>续费时长：{selectedProduct ? (selectedProduct.renewalUnit === 'permanent' ? '永久' : `${selectedProduct.renewalQuantity}${selectedProduct.renewalUnit === 'day' ? '天' : selectedProduct.renewalUnit === 'month' ? '个月' : '年'}`) : '未知'}</Text>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Text>订单金额：</Text>
                    <Text strong style={{ color: '#ff4d4f', fontSize: '18px' }}>¥{amount}</Text>
                  </div>
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff7e6', borderRadius: '4px' }}>
                    <Text type="warning">
                      支付链接生成失败，可能是支付配置不正确。请联系管理员检查支付配置，或稍后重试。
                    </Text>
                  </div>
                </div>
              ),
              onOk: () => {
                pollOrderStatus(orderId);
              },
            });
            pollOrderStatus(orderId);
          }
        }
      } else {
        message.error(response.data.error || '创建订单失败');
      }
    } catch (error: any) {
      console.error('创建续费订单错误:', error);
      message.error(error.response?.data?.error || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  const pollOrderStatus = useCallback(async (orderId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    let pollInterval: NodeJS.Timeout | null = null;

    const poll = async () => {
      attempts++;
      
      if (attempts >= maxAttempts) {
        message.warning('支付超时，请手动刷新页面查看订单状态');
        if (pollInterval) clearInterval(pollInterval);
        return;
      }

      try {
        const response = await authAPI.getRenewalOrderStatus(orderId);
        
        if (response.data.status === 200) {
          const { status } = response.data.data;
          
          if (status === 'paid') {
            if (pollInterval) clearInterval(pollInterval);
            
            setQrCodeModalVisible(false);
            setQrCodeData(null);
            
            message.success('支付成功！账户已续费');
            
            try {
              const profileResponse = await authAPI.getProfile();
              if (profileResponse.data.user) {
                updateUser(profileResponse.data.user);
                
                const newExpires = profileResponse.data.user.expires_at;
                if (newExpires) {
                  const now = new Date();
                  const expires = new Date(newExpires);
                  if (now <= expires) {
                  }
                }
              }
            } catch (error) {
              console.error('刷新用户信息失败:', error);
            }
            
            onSuccess();
            return;
          } else if (status === 'failed' || status === 'cancelled') {
            if (pollInterval) clearInterval(pollInterval);
            setQrCodeModalVisible(false);
            setQrCodeData(null);
            message.error('支付失败或已取消');
            return;
          }
        } else {
        }

        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setTimeout(poll, 5000);
      } catch (error: any) {
        console.error('查询订单状态错误:', error);
        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setTimeout(poll, 5000);
      }
    };

    poll();
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [updateUser, onSuccess]);

  useEffect(() => {
    if (qrCodeModalVisible && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsQrCodeExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [qrCodeModalVisible, countdown]);

  useEffect(() => {
    if (isQrCodeExpired && qrCodeData) {
      const autoCloseDelay = (config?.autoCloseDelay || 3) * 1000;
      const timeoutId = setTimeout(() => {
        const currentOrderId = qrCodeData.orderId;
        setQrCodeModalVisible(false);
        setQrCodeData(null);
        setIsQrCodeExpired(false);
        pollOrderStatus(currentOrderId);
      }, autoCloseDelay);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQrCodeExpired, qrCodeData, config]);

  const calculateRenewalDays = (unit: string, quantity: number) => {
    if (unit === 'permanent') {
      return -1; // 永久标记
    }
    
    const now = new Date();
    
    switch (unit) {
      case 'day':
        return quantity;
      case 'month':
        const startDate = new Date(now);
        const endDate = new Date(now);
        endDate.setMonth(endDate.getMonth() + quantity);
        const diffTime = endDate.getTime() - startDate.getTime();
        return Math.round(diffTime / (1000 * 60 * 60 * 24));
      case 'year':
        const startYear = new Date(now);
        const endYear = new Date(now);
        endYear.setFullYear(endYear.getFullYear() + quantity);
        const diffTimeYear = endYear.getTime() - startYear.getTime();
        return Math.round(diffTimeYear / (1000 * 60 * 60 * 24));
      default:
        const defaultStart = new Date(now);
        const defaultEnd = new Date(now);
        defaultEnd.setMonth(defaultEnd.getMonth() + quantity);
        const defaultDiff = defaultEnd.getTime() - defaultStart.getTime();
        return Math.round(defaultDiff / (1000 * 60 * 60 * 24));
    }
  };

  const calculatePrice = () => {
    if (!config || !selectedProduct) return '0.00';
    const pricePerUnit = selectedProduct.price;
    const discountRate = selectedProduct.discountRate || 1.00;
    const discountedPricePerUnit = pricePerUnit * discountRate;
    const totalPrice = discountedPricePerUnit * quantity;
    return totalPrice.toFixed(2);
  };

  const calculateNewExpiryDate = () => {
    if (!selectedProduct) return '';
    const renewalUnit = selectedProduct.renewalUnit || 'month';

    // 永久续费直接返回"永久"
    if (renewalUnit === 'permanent') {
      return '永久';
    }

    const productRenewalQuantity = selectedProduct.renewalQuantity || 1;
    const totalRenewalQuantity = productRenewalQuantity * quantity;

    // 按商品类型选择基准到期时间
    const now = dayjs();
    let baseExpiry: string | null | undefined;
    if (productType === 'card') {
      baseExpiry = cardExpireTime;
    } else if (productType === 'order_assistant') {
      baseExpiry = orderAssistantExpiresAt;
    } else {
      baseExpiry = user?.expires_at ? String(user.expires_at) : null;
    }

    let baseDate: dayjs.Dayjs;
    if (baseExpiry) {
      const expiresDate = dayjs(baseExpiry);
      // 如果已过期，从当前时间开始计算；如果未过期，从到期时间开始计算
      baseDate = expiresDate.isBefore(now) ? now : expiresDate;
    } else {
      baseDate = now;
    }

    let result;
    switch (renewalUnit) {
      case 'day':
        result = baseDate.add(totalRenewalQuantity, 'day');
        break;
      case 'month':
        result = baseDate.add(totalRenewalQuantity, 'month');
        break;
      case 'year':
        result = baseDate.add(totalRenewalQuantity, 'year');
        break;
      default:
        result = baseDate.add(totalRenewalQuantity, 'month');
    }
    return result.format('YYYY-MM-DD');
  };

  const calculateRenewalDaysForProduct = () => {
    if (!selectedProduct) return 0;
    const renewalUnit = selectedProduct.renewalUnit || 'month';
    const productRenewalQuantity = selectedProduct.renewalQuantity || 1;
    const totalRenewalQuantity = productRenewalQuantity * quantity;
    return calculateRenewalDays(renewalUnit, totalRenewalQuantity);
  };

  if (configLoading) {
    return (
      <Modal
        title={PRODUCT_TYPE_TITLES[productType]}
        open={visible}
        onCancel={onCancel}
        footer={null}
        centered
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>加载续费配置中...</Text>
          </div>
        </div>
      </Modal>
    );
  }

  if (!config) {
    return (
      <Modal
        title={PRODUCT_TYPE_TITLES[productType]}
        open={visible}
        onCancel={onCancel}
        footer={null}
        centered
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="danger">续费功能暂未配置，请联系管理员</Text>
        </div>
      </Modal>
    );
  }

  return (
    <>
    <Modal
      title={
        <Space>
          {productType === 'card' ? <IdcardOutlined /> : productType === 'order_assistant' ? <CustomerServiceOutlined /> : <CreditCardOutlined />}
          <span>{isExpired && productType === 'membership' ? '账户已到期，请续费' : PRODUCT_TYPE_TITLES[productType]}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      closable={true}
      maskClosable={true}
      footer={null}
      width={600}
      centered
    >
      {isExpired && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px', 
          background: '#fff7e6', 
          border: '1px solid #ffd591',
          borderRadius: '4px'
        }}>
          <Text type="warning" strong>
            ⚠️ 您的账户已到期，部分功能可能受限，建议续费后继续使用。
          </Text>
        </div>
      )}
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          paymentMethod: config.balancePaymentEnabled ? 'balance' : 
                        config.wechatEnabled ? 'wechat' : 
                        config.alipayEnabled ? 'alipay' : 'wechat',
          quantity: 1,
        }}
      >
        {/* 商品选择 */}
        {config.products && config.products.length > 0 && (
          <Form.Item
            label="选择商品"
            name="productId"
            rules={[{ required: true, message: '请选择商品' }]}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {config.products.map((product: Product) => {
                const isSelected = selectedProductId === product.id;
                return (
                  <div
                    key={product.id}
                    onClick={() => {
                      setSelectedProductId(product.id);
                      setSelectedProduct(product);
                      form.setFieldsValue({ productId: product.id });
                    }}
                    style={{
                      border: isSelected ? '2px solid #1890ff' : '2px dashed #d9d9d9',
                      borderRadius: '4px',
                      padding: '16px 24px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                      minWidth: '120px',
                      textAlign: 'center',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.backgroundColor = '#f0f9ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#d9d9d9';
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    <Text strong style={{ fontSize: '16px' }}>
                      {product.productName}(¥{product.price.toFixed(2)})
                    </Text>
                  </div>
                );
              })}
            </div>
            <input
              type="hidden"
              value={selectedProductId || ''}
              onChange={() => {}}
            />
          </Form.Item>
        )}
        
        {/* 数量选择 */}
        {selectedProduct && (
          <Form.Item
            label="数量"
            name="quantity"
            rules={[
              { required: true, message: '请输入数量' },
              { type: 'number', min: 1, max: 100, message: '数量必须在1-100之间' },
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              style={{ width: '100%' }}
              addonAfter="个"
              onChange={(value) => setQuantity(value || 1)}
            />
          </Form.Item>
        )}

        {/* 选中商品的详细信息 */}
        {selectedProduct && (
          <div style={{ marginBottom: '16px', padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary">单价：</Text>
              <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                ¥{selectedProduct.price.toFixed(2)}
              </Text>
              <Text type="secondary" style={{ marginLeft: '16px' }}>数量：</Text>
              <Text strong>{quantity}个</Text>
              {selectedProduct.rebateAmount > 0 && (
                <>
                  <Text type="secondary" style={{ marginLeft: '16px' }}>邀请返现：</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    ¥{selectedProduct.rebateAmount.toFixed(2)}
                  </Text>
                </>
              )}
              {!!selectedProduct.rebatePoints && selectedProduct.rebatePoints > 0 && (
                <>
                  <Text type="secondary" style={{ marginLeft: '16px' }}>返还专享积分：</Text>
                  <Text strong style={{ color: '#722ed1' }}>
                    {(selectedProduct.rebatePoints * POINTS_RATIO).toFixed(0)}积分（专享）
                  </Text>
                </>
              )}
            </div>
            <div>
              <Text type="secondary">总续费时长：</Text>
              <Text>
                {selectedProduct.renewalUnit === 'permanent' ? '永久' : (
                  <>
                    {selectedProduct.renewalQuantity * quantity}
                    {selectedProduct.renewalUnit === 'day' ? '天' : selectedProduct.renewalUnit === 'month' ? '个月' : '年'}
                    （{calculateRenewalDaysForProduct()}天）
                  </>
                )}
              </Text>
              <Text type="secondary" style={{ marginLeft: '16px' }}>{PRODUCT_TYPE_EXPIRY_LABELS[productType]}：</Text>
              <Text strong>{calculateNewExpiryDate()}</Text>
            </div>
          </div>
        )}

        <Form.Item
          label="支付方式"
          name="paymentMethod"
          rules={[{ required: true, message: '请选择支付方式' }]}
        >
          <Radio.Group
            onChange={(e) => setPaymentMethod(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space size="large" style={{ width: '100%' }}>
              {config.balancePaymentEnabled && (
                <Radio value="balance">
                  <Space>
                    <WalletOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                    <Text>积分支付({(balance * POINTS_RATIO).toFixed(0)}积分 ≈ ¥{balance.toFixed(2)})</Text>
                  </Space>
                </Radio>
              )}
              {config.wechatEnabled && (
                <Radio value="wechat">
                  <Space>
                    <WechatOutlined style={{ fontSize: '16px', color: '#07c160' }} />
                    <Text>微信支付</Text>
                  </Space>
                </Radio>
              )}
              {config.alipayEnabled && (
                <Radio value="alipay">
                  <Space>
                    <AlipayOutlined style={{ fontSize: '16px', color: '#1677ff' }} />
                    <Text>支付宝</Text>
                  </Space>
                </Radio>
              )}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Divider />

        {productType !== 'membership' && !boundCardKey && (
          <Alert
            type="warning"
            showIcon
            message="请先在首页绑定卡密后再续费"
            style={{ marginBottom: '16px' }}
          />
        )}

        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          {selectedProduct && (
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary">数量：</Text>
              <Text strong style={{ fontSize: '16px' }}>{quantity} 个</Text>
            </div>
          )}
          <Title level={4} style={{ margin: 0 }}>
            总计：¥{calculatePrice()}
          </Title>
        </div>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>取消</Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              icon={paymentMethod === 'balance' ? <WalletOutlined /> : <CreditCardOutlined />}
              disabled={(paymentMethod === 'balance' && balance < parseFloat(calculatePrice())) || (productType !== 'membership' && !boundCardKey)}
            >
              {paymentMethod === 'balance' ? `积分支付(需${(parseFloat(calculatePrice()) * POINTS_RATIO).toFixed(0)}积分)` : '立即支付'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>

    {/* 二维码支付弹窗 */}
    <Modal
      title="扫码支付"
      open={qrCodeModalVisible}
      onCancel={() => {
        setQrCodeModalVisible(false);
        setQrCodeData(null);
        if (qrCodeData) {
          pollOrderStatus(qrCodeData.orderId);
        }
      }}
      footer={null}
      width={400}
      centered
    >
      {qrCodeData && (
        <div style={{ textAlign: 'center' }}>
          {!isQrCodeExpired ? (
            <>
              <img
                src={qrCodeData.paymentType === 'alipay' 
                  ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData.qrCode)}`
                  : `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData.qrCode)}`
                }
                alt="支付二维码"
                style={{ width: '100%', maxWidth: 300, marginBottom: 16 }}
              />
              <div style={{ marginBottom: 8 }}>
                <Text strong>订单号：{qrCodeData.orderId}</Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>支付金额：¥{qrCodeData.amount || '0.00'}</Text>
              </div>
              <div>
                <Text type="secondary">二维码有效期：{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</Text>
              </div>
            </>
          ) : (
            <div style={{ padding: '40px 0' }}>
              <Text type="danger">二维码已过期</Text>
            </div>
          )}
        </div>
      )}
    </Modal>
    </>
  );
};

export default RenewalModal;


